import gql from 'graphql-tag';
import {
	CommentFragment,
	PageInfoFragment,
} from '@app/pages/cmms/graphql/cmms-fragments';

export const ChecklistFragment = gql`
	fragment ChecklistFragment on WorkOrderType {
		checklists {
			id
			template
		}
	}
`;

export const WorkOrderFragment = gql`
	fragment WorkOrderFragment on WorkOrderType {
		id
		name
		pk
		priority
		status
		updated
		workOrderId
		locations {
			id
			locationId
			locationType
			name
			pk
		}
		coordinates {
			x
			y
			z
		}
		deadline
		completedAt
		assigneeType
		created
		description
		category
		approver {
			contentType
			firstName
			lastName
			name
			psId
			smallAvatarUrl
			smallLogoUrl
		}
		approverType
		assets {
			id
			pk
			name
			location {
				id
				name
				type
			}
		}
		files {
			pk
			file {
				contentType
				name
				url
			}
			classificationsV2 {
				name
				type
				pk
			}
			createdBy {
				name
				lastName
				smallAvatarUrl
			}
			created
			type
			name
			importConfirmed
			importId
		}
		createdBy {
			contentType
			firstName
			lastName
			name
			psId
			smallAvatarUrl
			smallLogoUrl
		}
		assignee {
			contentType
			firstName
			lastName
			name
			psId
			smallAvatarUrl
			smallLogoUrl
		}
		events {
			created
			event
		}
		incidentWorkOrders(first: 100) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					priority
					id
					pk
					name
					status
					incidentId
					locations {
						id
						locationId
						locationType
						name
						pk
					}
				}
			}
		}
		workOrders {
			priority
			id
			pk
			name
			status
			workOrderId
		}
		requestWorkOrders(first: 100) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					priority
					id
					pk
					name
					status
					requestId
				}
			}
		}
		comments {
			...CommentFragment
		}
		routineData {
			excludeWeekends
			interval
			period
			monthSetup
			selectedDays
		}
		...ChecklistFragment
		deviationType
		costOfWork
		costAllocation
		timeframe
		labels {
			pk
			name
			type
			color
		}
		parentWorkOrder {
			pk
			name
		}
	}
	${PageInfoFragment}
	${CommentFragment}
	${ChecklistFragment}
`;

export const PaginatedWorkOrdersFragment = gql`
	fragment PaginatedWorkOrdersFragment on WorkOrderTypeConnection {
		pageInfo {
			...PageInfoFragment
		}
		edges {
			cursor
			node {
				...WorkOrderFragment
			}
		}
		totalCount
	}
	${PageInfoFragment}
	${WorkOrderFragment}
`;
export const CalendarWorkOrderFragment = gql`
	fragment CalendarWorkOrderFragment on WorkOrderTypeConnection {
		edges {
			node {
				pk
				name
				status
				deadline
				completedAt
			}
		}
	}
`;

//If you change the variables for this query - don't forget to update other ones below
export const WorkOrdersQuery = gql`
	query WorkOrdersQuery(
		$first: Int
		$last: Int
		$after: String
		$before: String
		$sortBy: String
		$assignee: [String]
		$createdBefore: DateTime
		$createdAfter: DateTime
		$updatedBefore: DateTime
		$updatedAfter: DateTime
		$deadlineBefore: DateTime
		$deadlineAfter: DateTime
		$description: String
		$name: String
		$priority: [Int]
		$status: [String]
		$isRepeatable: Boolean
		$workOrderId: [BigInt]
		$search: String
		$category: [String]
		$locations: [UUID]
		$calendarAfter: DateTime
		$calendarBefore: DateTime
		$isCalendarQuery: Boolean = false
		$costAllocation: [String]
		$timeframe: [String]
		$deviationType: [String]
		$costOfWork: Decimal
		$costOfWork_Gt: Decimal
		$costOfWork_Gte: Decimal
		$costOfWork_Isnull: Boolean
		$costOfWork_Lt: Decimal
		$costOfWork_Lte: Decimal
		$labels: [ID]
		$dateIntervalFilter: DatetimeIntervalFilterInput
	) {
		workOrders(
			first: $first
			after: $after
			before: $before
			last: $last
			sortBy: $sortBy
			assignee: $assignee
			createdBefore: $createdBefore
			createdAfter: $createdAfter
			updatedBefore: $updatedBefore
			updatedAfter: $updatedAfter
			deadlineBefore: $deadlineBefore
			deadlineAfter: $deadlineAfter
			description: $description
			name: $name
			priority_In: $priority
			status: $status
			isRepeatable: $isRepeatable
			workOrderId_In: $workOrderId
			search: $search
			category: $category
			locations: $locations
			calendarAfter: $calendarAfter
			calendarBefore: $calendarBefore
			costAllocation: $costAllocation
			timeframe: $timeframe
			deviationType: $deviationType
			costOfWork: $costOfWork
			costOfWork_Gt: $costOfWork_Gt
			costOfWork_Gte: $costOfWork_Gte
			costOfWork_Isnull: $costOfWork_Isnull
			costOfWork_Lt: $costOfWork_Lt
			costOfWork_Lte: $costOfWork_Lte
			labels_Id_In: $labels
			intervalFilter: $dateIntervalFilter
		) {
			...PaginatedWorkOrdersFragment @skip(if: $isCalendarQuery)
			...CalendarWorkOrderFragment @include(if: $isCalendarQuery)
		}
	}
	${PaginatedWorkOrdersFragment}
	${CalendarWorkOrderFragment}
`;

export const WorkOrderCalendarQuery = gql`
	query WorkOrderCalendarQuery(
		$sortBy: String
		$assignee: [String]
		$createdBefore: DateTime
		$createdAfter: DateTime
		$updatedBefore: DateTime
		$updatedAfter: DateTime
		$deadlineBefore: DateTime
		$deadlineAfter: DateTime
		$description: String
		$name: String
		$priority: [Int]
		$status: [String]
		$isRepeatable: Boolean
		$workOrderId: [BigInt]
		$search: String
		$category: [String]
		$locations: [UUID]
		$calendarAfter: DateTime!
		$calendarBefore: DateTime!
	) {
		workOrders(
			sortBy: $sortBy
			assignee: $assignee
			createdBefore: $createdBefore
			createdAfter: $createdAfter
			updatedBefore: $updatedBefore
			updatedAfter: $updatedAfter
			deadlineBefore: $deadlineBefore
			deadlineAfter: $deadlineAfter
			description: $description
			name: $name
			priority_In: $priority
			status: $status
			isRepeatable: $isRepeatable
			workOrderId_In: $workOrderId
			search: $search
			category: $category
			locations: $locations
			calendarAfter: $calendarAfter
			calendarBefore: $calendarBefore
		) {
			...CalendarWorkOrderFragment
		}
		futureRoutines(
			sortBy: $sortBy
			assignee: $assignee
			createdBefore: $createdBefore
			createdAfter: $createdAfter
			updatedBefore: $updatedBefore
			updatedAfter: $updatedAfter
			deadlineBefore: $deadlineBefore
			deadlineAfter: $deadlineAfter
			description: $description
			name: $name
			priority_In: $priority
			status: $status
			isRepeatable: $isRepeatable
			workOrderId_In: $workOrderId
			search: $search
			category: $category
			locations: $locations
			calendarAfter: $calendarAfter
			calendarBefore: $calendarBefore
		) {
			edges {
				node {
					pk
					name
					status
					deadline
					completedAt
					dates
				}
			}
		}
	}
	${CalendarWorkOrderFragment}
`;

//TODO use fragment with @skip + @include in main query
export const WorkOrderExportUrlQuery = gql`
	query WorkOrderExportUrlQuery(
		$sortBy: String
		$assignee: [String]
		$createdBefore: DateTime
		$createdAfter: DateTime
		$updatedBefore: DateTime
		$updatedAfter: DateTime
		$deadlineBefore: DateTime
		$deadlineAfter: DateTime
		$description: String
		$name: String
		$priority: [Int]
		$status: [String]
		$isRepeatable: Boolean
		$workOrderId: [BigInt]
		$search: String
		$category: [String]
		$locations: [UUID]
		$exportColumns: [String]
		$labels: [ID]
		$timeframe: [String]
	) {
		workOrders(
			sortBy: $sortBy
			assignee: $assignee
			createdBefore: $createdBefore
			createdAfter: $createdAfter
			updatedBefore: $updatedBefore
			updatedAfter: $updatedAfter
			deadlineBefore: $deadlineBefore
			deadlineAfter: $deadlineAfter
			description: $description
			name: $name
			priority_In: $priority
			status: $status
			isRepeatable: $isRepeatable
			workOrderId_In: $workOrderId
			search: $search
			category: $category
			locations: $locations
			exportColumns: $exportColumns
			labels_Id_In: $labels
			timeframe: $timeframe
		) {
			exportUrl
		}
	}
`;

export const FutureRoutinesQuery = gql`
	query FutureRoutinesQuery(
		$sortBy: String
		$assignee: [String]
		$createdBefore: DateTime
		$createdAfter: DateTime
		$updatedBefore: DateTime
		$updatedAfter: DateTime
		$deadlineBefore: DateTime
		$deadlineAfter: DateTime
		$description: String
		$name: String
		$priority: [Int]
		$status: [String]
		$isRepeatable: Boolean
		$workOrderId: [BigInt]
		$search: String
		$category: [String]
		$locations: [UUID]
		$calendarAfter: DateTime!
		$calendarBefore: DateTime!
		$labels: [ID]
	) {
		futureRoutines(
			sortBy: $sortBy
			assignee: $assignee
			createdBefore: $createdBefore
			createdAfter: $createdAfter
			updatedBefore: $updatedBefore
			updatedAfter: $updatedAfter
			deadlineBefore: $deadlineBefore
			deadlineAfter: $deadlineAfter
			description: $description
			name: $name
			priority_In: $priority
			status: $status
			isRepeatable: $isRepeatable
			workOrderId_In: $workOrderId
			search: $search
			category: $category
			locations: $locations
			calendarAfter: $calendarAfter
			calendarBefore: $calendarBefore
			labels_Id_In: $labels
		) {
			edges {
				node {
					id
					pk
					name
					status
					deadline
					completedAt
					dates
					category
					created
					priority
					updated
					routineData {
						excludeWeekends
						interval
						period
						monthSetup
						selectedDays
					}
					assignee {
						contentType
						firstName
						lastName
						name
						psId
						smallAvatarUrl
						smallLogoUrl
					}
					createdBy {
						contentType
						firstName
						lastName
						name
						psId
						smallAvatarUrl
						smallLogoUrl
					}
				}
			}
		}
	}
`;

export const GetWorkOrderQuery = gql`
	query GetWorkOrderQuery($id: UUID) {
		workOrder(id: $id) {
			...WorkOrderFragment
		}
	}
	${WorkOrderFragment}
`;

export const WorkSearchOrdersQuery = gql`
	query WorkSearchOrdersQuery(
		$search: String
		$first: Int
		$last: Int
		$after: String
		$before: String
	) {
		workOrders(
			search: $search
			first: $first
			last: $last
			after: $after
			before: $before
		) {
			pageInfo {
				...PageInfoFragment
			}
			edges {
				cursor
				node {
					...WorkOrderFragment
				}
			}
			totalCount
		}
	}
	${PageInfoFragment}
	${WorkOrderFragment}
`;
